import { Account } from '../data/schema/accountSchema'
import { KnowledgeSpaceReference } from '../types'
/**
 * Calculate average account health as a 0-100 score
 * Green = 100, Yellow = 60, Red = 20
 */
export function calculateAverageHealth(accounts: Account[]): number {
  if (accounts.length === 0) return 0
  const healthScores = {
    Green: 100,
    Yellow: 60,
    Red: 20,
  }
  const totalScore = accounts.reduce(
    (sum, account) => sum + healthScores[account.health],
    0,
  )
  return Math.round(totalScore / accounts.length)
}
/**
 * Count spaces by visibility type
 */
export function countSpacesByVisibility(accounts: Account[]): {
  Internal: number
  Partner: number
} {
  const spacesMap = new Map<string, KnowledgeSpaceReference>()
  accounts.forEach((account) => {
    account.bots.forEach((bot) => {
      bot.bound_spaces.forEach((space) => {
        if (!spacesMap.has(space.id)) {
          spacesMap.set(space.id, space)
        }
      })
    })
  })
  const spaces = Array.from(spacesMap.values())
  return {
    Internal: spaces.filter((s) => s.visibility === 'Internal').length,
    Partner: spaces.filter((s) => s.visibility === 'Partner').length,
  }
}
/**
 * Count accounts by health status
 */
export function countAccountsByHealth(
  accounts: Account[],
): { name: string; value: number; color: string }[] {
  const greenCount = accounts.filter((a) => a.health === 'Green').length
  const yellowCount = accounts.filter((a) => a.health === 'Yellow').length
  const redCount = accounts.filter((a) => a.health === 'Red').length
  return [
    { name: 'Green', value: greenCount, color: '#10b981' },
    { name: 'Yellow', value: yellowCount, color: '#f59e0b' },
    { name: 'Red', value: redCount, color: '#ef4444' },
  ]
}
/**
 * Count how many accounts use each product/service
 */
export function countServicesUsage(
  accounts: Account[],
): { service: string; count: number }[] {
  const serviceMap = new Map<string, number>()
  accounts.forEach((account) => {
    account.products_services.forEach((service) => {
      serviceMap.set(service, (serviceMap.get(service) || 0) + 1)
    })
  })
  return Array.from(serviceMap.entries())
    .map(([service, count]) => ({ service, count }))
    .sort((a, b) => b.count - a.count)
}
/**
 * Get total unique Knowledge Spaces count
 */
export function getTotalSpacesCount(accounts: Account[]): number {
  const spacesMap = new Map<string, boolean>()
  accounts.forEach((account) => {
    account.bots.forEach((bot) => {
      bot.bound_spaces.forEach((space) => {
        spacesMap.set(space.id, true)
      })
    })
  })
  return spacesMap.size
}
/**
 * Generate mock activity log entries
 */
export function generateActivityLog(): {
  id: string
  timestamp: string
  event: string
  category: 'Sync' | 'Update' | 'Alert'
}[] {
  return [
    {
      id: 'act-1',
      timestamp: '2025-11-05T14:00:00Z',
      event: 'Synced 6 Knowledge Spaces across all accounts',
      category: 'Sync',
    },
    {
      id: 'act-2',
      timestamp: '2025-11-04T10:20:00Z',
      event: 'Added new asset to Target Partner View',
      category: 'Update',
    },
    {
      id: 'act-3',
      timestamp: '2025-11-04T09:15:00Z',
      event: 'Account Health Updated – Capital One moved to Red',
      category: 'Alert',
    },
    {
      id: 'act-4',
      timestamp: '2025-11-03T16:30:00Z',
      event: 'New chatbot deployed for REI Account Core',
      category: 'Update',
    },
    {
      id: 'act-5',
      timestamp: '2025-11-03T11:45:00Z',
      event: 'Synced National Geographic Partner View',
      category: 'Sync',
    },
    {
      id: 'act-6',
      timestamp: '2025-11-02T14:20:00Z',
      event: 'SLA Alert triggered for Capital One',
      category: 'Alert',
    },
    {
      id: 'act-7',
      timestamp: '2025-11-02T10:00:00Z',
      event: 'Updated pricing matrix for Walgreens Account Core',
      category: 'Update',
    },
    {
      id: 'act-8',
      timestamp: '2025-11-01T15:30:00Z',
      event: 'Full system sync completed – 12 spaces updated',
      category: 'Sync',
    },
  ]
}
